import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { IconArrowRight, IconCheck, IconExternalLink, IconLock } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEMO_FACILITATOR_FEATURES,
  DEMO_FEEDBACK_QUESTIONS,
  DEMO_FINAL_COPY,
  DEMO_GUIDANCE,
  RESOURCE_KEYS,
  TEST_OFFER_BADGE,
  type DemoFacilitatorFeature,
  type DemoFeedbackInput,
  type DemoFeedbackQuestion,
  type DemoFeedbackView,
  type GameDrawnCardView,
  type GameParticipantView,
  type GameSessionView,
} from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';
import { ResourceCounter } from '@/components/domain/ResourceCounter';
import { toUserMessage } from '@/lib/api';
import { answerDemoFeedback, reportDemoOfferClick } from '../api';
import type { DemoViewer } from './demo-step';

type JourneyCard = { card: GameDrawnCardView; origin: string };

/** Откуда карта — по данным самой карты, а не по колоде. */
function originOf(card: GameDrawnCardView): string {
  if (card.reason === 'start_talent') return DEMO_FINAL_COPY.birthTalent;
  if (card.reason === 'talent_cell') return DEMO_FINAL_COPY.cellTalent;
  if (card.reason === 'event_reward' || card.reason === 'bazaar_exchange') return DEMO_FINAL_COPY.rewardTalent;
  return card.deckTitle;
}

/**
 * Карты игрока по этапам пути: талант, страх, роль-маска.
 *
 * Талант от рождения стоит отдельно, первым. Дополнительные таланты с клеток
 * «Т» лежат в том секторе, где их вытянули, — сектор записан в самой карте.
 * Раньше все таланты попадали под «Маяк», хотя часть была получена у Дракона
 * или в Замке.
 */
function journeyOf(session: GameSessionView, participantId: string) {
  const own = session.drawnCards.filter((card) => card.drawnByParticipantId === participantId);
  const tag = (cards: GameDrawnCardView[]): JourneyCard[] =>
    cards.map((card) => ({ card, origin: originOf(card) }));
  const birth = own.filter((card) => card.reason === 'start_talent');
  const stages = (session.demo?.routeSectorIds ?? []).map((sectorId) => ({
    sectorId,
    /* Главная карта этапа: у Маяка — талант от рождения, у остальных — карта сектора. */
    main: tag(sectorId === 'lighthouse'
      ? birth
      : own.filter((card) => card.reason === 'sector_event' && card.sectorId === sectorId)),
    extra: tag(own.filter((card) => card.reason === 'talent_cell' && card.sectorId === sectorId)),
  }));
  const rewards = tag(own.filter((card) => card.reason === 'event_reward' || card.reason === 'bazaar_exchange'));
  return { stages, rewards };
}

function CardStrip({
  cards,
  onZoom,
  empty,
}: {
  cards: JourneyCard[];
  onZoom: (item: JourneyCard) => void;
  empty?: string;
}) {
  if (cards.length === 0) {
    return empty ? <Text size="sm" c="var(--text-muted)">{empty}</Text> : null;
  }
  return (
    <ScrollArea type="auto">
      <Group gap="sm" wrap="nowrap" align="flex-start" pb={4}>
        {cards.map((item) => (
          <Stack key={item.card.id} gap={4} align="center" w={96}>
            <PlayingCard
              frontImage={item.card.card.frontImage}
              backImage={item.card.card.backImage}
              faceUp={item.card.revealed}
              label={`Рассмотреть крупно: ${item.origin}`}
              width={84}
              onClick={() => onZoom(item)}
            />
            <Text size="xs" c="var(--text-muted)" ta="center" lh={1.2}>{item.origin}</Text>
          </Stack>
        ))}
      </Group>
    </ScrollArea>
  );
}

/**
 * Итог одного игрока — только то, что в партии действительно произошло: если
 * карты сектора нет, блок так и говорит, а не придумывает её.
 */
function PlayerJourney({
  session,
  player,
  onZoom,
}: {
  session: GameSessionView;
  player: GameParticipantView;
  onZoom: (item: JourneyCard) => void;
}) {
  const progress = session.demo?.progress.find((item) => item.participantId === player.id);
  const journey = journeyOf(session, player.id);
  return (
    <Stack gap="sm" data-testid={`demo-journey-${player.id}`}>
      {journey.stages.map((stage) => {
        const sector = session.game.board?.sectors.find((item) => item.id === stage.sectorId);
        const done = progress?.completedSectorIds.includes(stage.sectorId) ?? false;
        return (
          <Stack key={stage.sectorId} gap={6} data-testid={`demo-stage-${stage.sectorId}`}>
            <Group gap="xs">
              <Badge variant={done ? 'filled' : 'light'} color={done ? 'teal' : 'gray'}>
                {DEMO_FINAL_COPY.stages[stage.sectorId]}
              </Badge>
              <Text size="sm" fw={600}>{sector?.title}</Text>
            </Group>
            <CardStrip
              cards={[...stage.main, ...stage.extra]}
              onZoom={onZoom}
              empty={DEMO_FINAL_COPY.missingCard}
            />
          </Stack>
        );
      })}
      {journey.rewards.length > 0 ? (
        <Stack gap={6}>
          <Text size="sm" fw={600}>Таланты за ответы</Text>
          <CardStrip cards={journey.rewards} onZoom={onZoom} />
        </Stack>
      ) : null}
      <Group gap={6} wrap="wrap">
        {RESOURCE_KEYS.map((resource) => (
          <ResourceCounter key={resource} compact resource={resource} value={player.resources?.[resource] ?? 0} />
        ))}
        <Badge variant="light" color="gold">Луч: {player.raySlots.length} из 6</Badge>
      </Group>
    </Stack>
  );
}

/**
 * Карта крупно — только просмотр: ни новых карт, ни наград, партия не
 * меняется. Закрытие возвращает к итогу, который остаётся открытым под ней.
 */
function ZoomedCard({ item, onClose }: { item: JourneyCard | null; onClose: () => void }) {
  const card = item?.card;
  const number = card?.revealed && card.card.position !== null ? ` · карта ${card.card.position}` : '';
  return (
    <Modal
      opened={Boolean(item)}
      onClose={onClose}
      title={item ? item.origin : ''}
      centered
      size="auto"
      zIndex={400}
      data-testid="demo-zoomed-card"
    >
      {card ? (
        <Stack align="center" gap="sm">
          <PlayingCard
            frontImage={card.card.frontImage}
            backImage={card.card.backImage}
            faceUp={card.revealed}
            label={`${card.deckTitle}${number}`}
            width={300}
          />
          <Text size="sm" fw={600}>{card.deckTitle}{number}</Text>
          <Button variant="light" onClick={onClose}>Вернуться к итогу</Button>
        </Stack>
      ) : null}
    </Modal>
  );
}

/**
 * Сектора, закрытые в демо: название, короткое описание и сказка сектора — без
 * карт. Сказка — короткие авторские строки; они показываются как есть, по
 * строке, и на телефоне переносятся только по ширине.
 */
export function DemoClosedSectors({
  session,
  selected,
  onSelect,
  hint = DEMO_FINAL_COPY.closedSectorsHint,
}: {
  session: GameSessionView;
  selected: string | null;
  onSelect: (sectorId: string) => void;
  /** В окне итога поля не видно — «ниже»; рядом с полем — «на поле». */
  hint?: string;
}) {
  const closed = session.demo?.closedSectors ?? [];
  if (closed.length === 0) return null;
  const current = closed.find((sector) => sector.id === selected);
  return (
    <Stack gap="xs" data-testid="demo-closed-sectors">
      <Text fw={700}>{DEMO_FINAL_COPY.closedSectorsTitle}</Text>
      <Text size="sm" c="var(--text-muted)">{hint}</Text>
      <Group gap="xs" wrap="wrap">
        {closed.map((sector) => (
          <Button
            key={sector.id}
            size="xs"
            variant={sector.id === selected ? 'filled' : 'light'}
            leftSection={<IconLock size={13} />}
            onClick={() => onSelect(sector.id)}
          >
            {sector.title}
          </Button>
        ))}
      </Group>
      {current ? (
        <Paper withBorder p="sm" data-testid="demo-closed-sector-description">
          <Text fw={600} size="sm">{current.title}</Text>
          <Text size="sm" c="var(--text-muted)">{current.description}</Text>
          {current.tale ? (
            <Text
              size="sm"
              mt="xs"
              fs="italic"
              lh={1.6}
              style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}
              data-testid="demo-closed-sector-tale"
            >
              {current.tale}
            </Text>
          ) : null}
        </Paper>
      ) : null}
    </Stack>
  );
}

/**
 * Куда ведёт главная кнопка (ТЗ демо, п. 8): у кого полная игра уже есть —
 * к ней, без повторной покупки; у кого нет — на страницу оформления. Какую
 * именно — тестовую или рабочую, — решает режим приёма оплат на сервере
 * (docs/PAYMENTS.md, §10); тестовое оформление помечено. Если адрес для
 * текущего режима не настроен, кнопки нет: пустая ссылка хуже, чем честная
 * фраза.
 */
function FullGameCta({ session }: { session: GameSessionView }) {
  const demo = session.demo!;
  if (!demo.fullGame) return null;
  /* Какая это кнопка — своя игра или покупка, — сервер решает сам (docs/ANALYTICS.md). */
  const report = () => reportDemoOfferClick(session.id);
  if (demo.fullGame.accessible) {
    return (
      <Button
        component={Link}
        to={`/games/${demo.fullGame.slug}`}
        onClick={report}
        rightSection={<IconArrowRight size={16} />}
        data-testid="demo-cta"
      >
        {DEMO_FINAL_COPY.ctaOwned}
      </Button>
    );
  }
  if (demo.offerUrl) {
    return (
      <Group gap="xs" wrap="wrap">
        <Button
          component="a"
          href={demo.offerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={report}
          color="gold"
          rightSection={<IconExternalLink size={16} />}
          data-testid="demo-cta"
        >
          {demo.scenario === 'solo' ? DEMO_FINAL_COPY.ctaSolo : DEMO_FINAL_COPY.ctaFacilitator}
        </Button>
        {demo.offerIsTest ? (
          <Badge size="xs" variant="light" color="grape" data-testid="demo-cta-test">{TEST_OFFER_BADGE}</Badge>
        ) : null}
      </Group>
    );
  }
  return <Text size="sm" c="var(--text-muted)" data-testid="demo-no-offer">{DEMO_FINAL_COPY.noOffer}</Text>;
}

/**
 * Два необязательных вопроса после итога (концепция, п. 1.9; ТЗ демо, п. 8).
 *
 * Ответ сохраняется сразу по нажатию — формы без сохранения ТЗ не допускает;
 * передумавший нажимает другой вариант, и он заменяет прежний. Только
 * владельцу партии: гость по приглашению вопросов не получает.
 *
 * Блок короткий и стоит под кратким итогом, до подробностей пути. Последним
 * блоком длинного окна, за кнопками перехода, его не замечали: 15 октября
 * владелец прошёл демо и вопросов не увидел вовсе.
 */
function DemoFeedback({ session, initial }: { session: GameSessionView; initial: DemoFeedbackView }) {
  const [answers, setAnswers] = useState<DemoFeedbackView>(initial);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const save = (input: DemoFeedbackInput) => {
    setPending(true);
    setError(null);
    answerDemoFeedback(session.id, input)
      .then(setAnswers, setError)
      .finally(() => setPending(false));
  };
  const questions = Object.keys(DEMO_FEEDBACK_QUESTIONS) as DemoFeedbackQuestion[];

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      data-testid="demo-feedback"
      style={{ background: 'var(--surface-accent-soft)', borderColor: 'var(--border-subtle)' }}
    >
      <Stack gap="xs">
        <div>
          <Text fw={700}>{DEMO_FINAL_COPY.feedbackTitle}</Text>
          <Text size="sm" c="var(--text-muted)">{DEMO_FINAL_COPY.feedbackHint}</Text>
        </div>
        {questions.map((question) => (
          /* Вопрос и ответы — одной строкой: оба вопроса занимают две строки, а не шесть. */
          <Group key={question} gap="xs" justify="space-between" wrap="wrap">
            <Text size="sm" style={{ flex: '1 1 14rem' }}>{DEMO_FEEDBACK_QUESTIONS[question].text}</Text>
            <Group gap="xs">
              {Object.entries(DEMO_FEEDBACK_QUESTIONS[question].answers).map(([answer, label]) => {
                const chosen = answers[question] === answer;
                return (
                  <Button
                    key={answer}
                    size="xs"
                    variant={chosen ? 'filled' : 'light'}
                    aria-pressed={chosen}
                    disabled={pending}
                    onClick={() => save({ question, answer } as DemoFeedbackInput)}
                    data-testid={`demo-feedback-${question}-${answer}`}
                  >
                    {label}
                  </Button>
                );
              })}
            </Group>
          </Group>
        ))}
        {error ? (
          <Text size="sm" c="red">{toUserMessage(error)}</Text>
        ) : answers.clear || answers.full ? (
          <Text size="sm" c="var(--text-muted)" data-testid="demo-feedback-saved">{DEMO_FINAL_COPY.feedbackSaved}</Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

export function DemoFinal({
  session,
  viewer,
  opened,
  onClose,
  selectedSector,
  onSelectSector,
}: {
  session: GameSessionView;
  viewer: DemoViewer;
  opened: boolean;
  onClose: () => void;
  selectedSector: string | null;
  onSelectSector: (sectorId: string) => void;
}) {
  const [zoomed, setZoomed] = useState<JourneyCard | null>(null);
  const demo = session.demo;
  if (!demo) return null;
  const players = session.participants.filter((item) => item.kind !== 'facilitator');
  /* Гостю — свой путь; ведущему — путь каждого; в одиночной партии игрок один. */
  const shown = viewer.role === 'guest'
    ? players.filter((item) => item.id === viewer.participantId)
    : players;
  const host = viewer.role === 'facilitator';
  const used = new Set(demo.usedFeatures);
  const features = Object.keys(DEMO_FACILITATOR_FEATURES) as DemoFacilitatorFeature[];
  /* Гостю ни опроса, ни предложения: партия не его, покупателем он не является. */
  const feedback = viewer.role === 'guest' ? null : demo.feedback;
  const showCta = viewer.role !== 'guest' && demo.fullGame !== null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} fz="xl">{DEMO_FINAL_COPY.title}</Text>}
      size="xl"
      centered
      /*
        Партия часто завершается открытием последней карты — в её же окне.
        Итог — главное событие, поэтому он поверх: закрыв его, человек снова
        увидит карту, на которой закончил.
      */
      zIndex={300}
      data-testid="demo-final"
    >
      <Stack gap="lg">
        {demo.endedEarly ? (
          <Alert color="gold" variant="light">{DEMO_FINAL_COPY.earlyFinish}</Alert>
        ) : null}

        <Text>
          {viewer.role === 'solo'
            ? DEMO_GUIDANCE.solo.final
            : host
              ? DEMO_GUIDANCE.facilitator.final
              : 'Вы прошли ознакомительную партию: талант, страх, роль-маска.'}
        </Text>

        {/*
          Опрос и переход к полной игре — сразу под кратким итогом, до
          подробностей пути: внизу длинного окна их приходилось искать.
        */}
        {feedback ? <DemoFeedback session={session} initial={feedback} /> : null}
        {showCta ? (
          <Group>
            <FullGameCta session={session} />
          </Group>
        ) : null}
        {feedback || showCta ? (
          <Divider label={DEMO_FINAL_COPY.detailsTitle} labelPosition="left" />
        ) : null}

        {shown.length > 0 ? (
          <Text size="sm" c="var(--text-muted)">{DEMO_FINAL_COPY.zoomHint}</Text>
        ) : null}

        {shown.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">За столом никто не играл — карт и ресурсов в этой партии нет.</Text>
        ) : shown.map((player) => (
          <Stack key={player.id} gap="xs">
            {shown.length > 1 || host ? <Text fw={700}>{player.displayName}</Text> : null}
            <PlayerJourney session={session} player={player} onZoom={setZoomed} />
            <Divider />
          </Stack>
        ))}

        {host ? (
          <Stack gap={6} data-testid="demo-used-features">
            <Text fw={700}>Что вы попробовали как ведущий</Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={6}>
              {features.map((feature) => (
                <Group key={feature} gap={6} wrap="nowrap">
                  {used.has(feature)
                    ? <IconCheck size={15} color="var(--accent)" />
                    : <Text size="sm" c="var(--text-subtle)" w={15} ta="center">·</Text>}
                  <Text size="sm" c={used.has(feature) ? undefined : 'var(--text-muted)'}>
                    {DEMO_FACILITATOR_FEATURES[feature]}
                    {used.has(feature) ? '' : ' — в этой партии не использовалось'}
                  </Text>
                </Group>
              ))}
            </SimpleGrid>
          </Stack>
        ) : null}

        <DemoClosedSectors session={session} selected={selectedSector} onSelect={onSelectSector} />

        <Group>
          <Button variant="subtle" onClick={onClose}>{DEMO_FINAL_COPY.close}</Button>
        </Group>
      </Stack>
      <ZoomedCard item={zoomed} onClose={() => setZoomed(null)} />
    </Modal>
  );
}
